/**
 * @component ry-embed
 * @category  display
 * @summary   Lazy-loaded video / iframe embed (Fomantic UI `.ui.embed` equivalent). Renders a placeholder image with a play button; on click, swaps in the actual `<iframe>` for YouTube, Vimeo, or an arbitrary URL. Saves bandwidth + load time until the user opts in. Fires `ry:play` on activation.
 *
 * Reference: Fomantic UI's embed module. ry-embed keeps the placeholder +
 * lazy-iframe pattern but rebuilds it on web components. Supports
 * `source="youtube"` / `source="vimeo"` (with `id="..."`) for known providers
 * or `url="..."` for arbitrary iframe content.
 *
 * @attribute source       'youtube' | 'vimeo' | 'iframe'?    Provider. When set with `[id]`, ry-embed builds the proper embed URL. Set `source="iframe"` (or omit) and pass `[url]` for arbitrary embed sources.
 * @attribute id           string?              Provider video ID. Combined with `[source]` to build the embed URL. Ignored when `[url]` is set.
 * @attribute url          string?              Arbitrary iframe URL. Overrides the source/id pair.
 * @attribute placeholder  string?              Thumbnail image URL shown before playback. For YouTube, ry-embed auto-derives a thumbnail from the `[id]` when `[placeholder]` is omitted.
 * @attribute autoplay     boolean   default=false   When activated, append autoplay parameters to the embed URL so the video starts on iframe load. Matches Fomantic's autoplay default behavior.
 * @attribute icon         string?   default="▶"     Play-button glyph rendered over the placeholder. Use any single character or short string.
 *
 * @property  active       boolean   Read-only — true after the iframe has been swapped in (after click/`play()`).
 *
 * @method    play()       Programmatically swap in the iframe. Fires `ry:play`. No-op if already active.
 * @method    reset()      Swap back to placeholder + play button. Fires `ry:reset`.
 *
 * @event ry:beforeplay
 *        detail: { src: string }
 *        fires:  at the top of `play()` — click, Enter/Space, or `el.play()` — before the iframe is inserted or the state changes. `detail.src` is the iframe URL about to load.
 *        cancelable: preventDefault() keeps the placeholder (no iframe, no network); `ry:play` does not fire. The consent gate.
 *
 * @event ry:play
 *        detail: { src: string }
 *        fires:  when the iframe is inserted. `detail.src` is the resolved iframe URL.
 *
 * @event ry:reset
 *        detail: {}
 *        fires:  when `reset()` is called and the embed returns to placeholder state.
 *
 * @state     `data-ry-state`: `"active"` once the iframe is showing; otherwise unset.
 *
 * @content   None — `<ry-embed>` is configured entirely via attributes. innerHTML is replaced at setup.
 *
 * @dom (placeholder state, default)
 *   ry-embed[source?][id?][url?][placeholder?][autoplay?][icon?]   role="button"   tabindex="0"   aria-label="Play video"
 *     > div.ry-embed__placeholder   [data-ry-target="placeholder"]   style="background-image: url(...)"
 *       > span.ry-embed__icon        [data-ry-target="icon"]   aria-hidden="true"   {icon glyph}
 *
 * @dom (active state, post-click)
 *   ry-embed[data-ry-state="active"]   role="region"   aria-label="Video player"
 *     > iframe.ry-embed__iframe       [data-ry-target="iframe"]   src=resolved-url
 *
 * @gotcha  YouTube thumbnail auto-derivation uses `https://i.ytimg.com/vi/{id}/hqdefault.jpg`. If the video lacks that resolution, the placeholder fails to load and the embed still renders the play button. Pass an explicit `[placeholder]` for non-standard hosts or private videos.
 *
 * @gotcha  Vimeo does NOT have a stable thumbnail URL pattern — `[placeholder]` is effectively required for `source="vimeo"`. If omitted, the placeholder background is empty.
 *
 * @gotcha  The host's ARIA role + label changes between states: `role="button"` + `aria-label="Play video"` BEFORE play, `role="region"` + `aria-label="Video player"` AFTER. Consumer-set aria-label is preserved across the transition.
 *
 * @gotcha  `[autoplay]` ONLY auto-plays the iframe content after the user has clicked (or `play()` was called). It does NOT skip the placeholder — that would violate browser autoplay policies. The user MUST opt in.
 *
 * @gotcha  Calling `play()` when already active is a no-op (no duplicate `ry:play`). Calling `reset()` when already at placeholder is a no-op (no `ry:reset`).
 *
 * @example youtube
 * ```html
 * <ry-embed source="youtube" id="dQw4w9WgXcQ"></ry-embed>
 * ```
 *
 * @example vimeo
 * ```html
 * <ry-embed source="vimeo" id="217124847" placeholder="/thumb.jpg"></ry-embed>
 * ```
 *
 * @example iframe-url
 * ```html
 * <ry-embed url="https://example.com/embed/abc" placeholder="/thumb.jpg"></ry-embed>
 * ```
 *
 * @example with-autoplay
 * ```html
 * <ry-embed source="youtube" id="dQw4w9WgXcQ" autoplay></ry-embed>
 * ```
 *
 * @example custom-icon
 * ```html
 * <ry-embed source="youtube" id="dQw4w9WgXcQ" icon="►"></ry-embed>
 * ```
 *
 * @tutorial mount-renders-placeholder
 * ```js
 * const el = mount('<ry-embed source="youtube" id="abc123"></ry-embed>');
 * await expectEqual(!!el.querySelector('[data-ry-target="placeholder"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="icon"]'), true);
 * await expectEqual(el.querySelector('[data-ry-target="iframe"]'), null, 'no iframe yet');
 * ```
 *
 * @tutorial youtube-auto-thumbnail
 * ```js
 * // Wrong assumption: thumbnail must be set manually. YouTube source
 * // auto-derives from the ID via i.ytimg.com.
 * const el = mount('<ry-embed source="youtube" id="abc123"></ry-embed>');
 * const placeholder = el.querySelector('[data-ry-target="placeholder"]');
 * const bg = placeholder.style.backgroundImage;
 * await expectEqual(bg.includes('abc123'), true, 'youtube id used in thumb URL');
 * await expectEqual(bg.includes('i.ytimg.com'), true, 'i.ytimg.com host');
 * ```
 *
 * @tutorial click-activates-iframe
 * ```js
 * const el = mount('<ry-embed source="youtube" id="abc123"></ry-embed>');
 * const fired = [];
 * el.addEventListener('ry:play', (e) => fired.push(e.detail.src));
 * el.click();
 * await expectAttr(el, 'data-ry-state', 'active');
 * await expectEqual(el.active, true);
 * await expectEqual(!!el.querySelector('[data-ry-target="iframe"]'), true, 'iframe inserted');
 * await expectEqual(fired.length, 1);
 * await expectEqual(fired[0].includes('youtube.com/embed/abc123'), true);
 * ```
 *
 * @tutorial keyboard-activation
 * ```js
 * // tabindex=0 + role=button — Enter/Space activate just like a button.
 * const el = mount('<ry-embed source="youtube" id="abc123"></ry-embed>');
 * await expectAttr(el, 'tabindex', '0');
 * await expectAttr(el, 'role', 'button');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
 * await expectAttr(el, 'data-ry-state', 'active');
 * ```
 *
 * @tutorial url-overrides-source-id
 * ```js
 * // Wrong assumption: source + id always wins. An explicit url overrides.
 * const el = mount('<ry-embed source="youtube" id="abc" url="https://custom.example.com/v/x"></ry-embed>');
 * el.play();
 * const iframe = el.querySelector('[data-ry-target="iframe"]');
 * await expectAttr(iframe, 'src', 'https://custom.example.com/v/x', 'url wins over source+id');
 * ```
 *
 * @tutorial autoplay-appends-to-embed-url
 * ```js
 * const el = mount('<ry-embed source="youtube" id="abc" autoplay></ry-embed>');
 * el.play();
 * const iframe = el.querySelector('[data-ry-target="iframe"]');
 * await expectEqual(iframe.getAttribute('src').includes('autoplay=1'), true, 'autoplay=1 param appended');
 * ```
 *
 * @tutorial reset-returns-to-placeholder
 * ```js
 * const el = mount('<ry-embed source="youtube" id="abc"></ry-embed>');
 * el.play();
 * const fired = [];
 * el.addEventListener('ry:reset', () => fired.push(1));
 * el.reset();
 * await expectEqual(el.dataset.ryState, undefined);
 * await expectEqual(el.active, false);
 * await expectEqual(!!el.querySelector('[data-ry-target="placeholder"]'), true, 'placeholder restored');
 * await expectEqual(el.querySelector('[data-ry-target="iframe"]'), null, 'iframe removed');
 * await expectEqual(fired.length, 1);
 * ```
 *
 * @tutorial play-is-idempotent
 * ```js
 * // Wrong assumption: play() can be called repeatedly to "restart" the
 * // video. It only swaps the iframe in once — subsequent calls are no-ops.
 * const el = mount('<ry-embed source="youtube" id="abc"></ry-embed>');
 * const fired = [];
 * el.addEventListener('ry:play', () => fired.push(1));
 * el.play(); el.play(); el.play();
 * await expectEqual(fired.length, 1, 'ry:play fired only once');
 * ```
 *
 * @tutorial beforeplay-veto
 * ```js
 * // The consent gate: preventDefault() on ry:beforeplay keeps the placeholder
 * // and no iframe is requested — whatever triggered it (click, key, play()).
 * const el = mount('<ry-embed source="youtube" id="abc"></ry-embed>');
 * const played = [];
 * el.addEventListener('ry:play', () => played.push(1));
 * el.addEventListener('ry:beforeplay', (e) => e.preventDefault(), { once: true });
 * el.click();
 * await expectEqual(el.active, false, 'vetoed play leaves the placeholder');
 * await expectEqual(el.querySelector('[data-ry-target="iframe"]'), null, 'no iframe inserted');
 * await expectEqual(played.length, 0, 'ry:play did not fire');
 * el.play();
 * await expectEqual(el.active, true, 'without the veto, play() activates');
 * await expectEqual(played.length, 1);
 * ```
 *
 * @tutorial icon-override-respected
 * ```js
 * const el = mount('<ry-embed source="youtube" id="abc" icon="►"></ry-embed>');
 * const iconEl = el.querySelector('[data-ry-target="icon"]');
 * await expectEqual(iconEl.textContent, '►', 'icon attribute respected');
 * ```
 *
 * @see ry-image
 * @see ry-card
 */

import { RyElement } from '../core/ry-element.js';

interface PlayDetail {
  src: string;
}

export class RyEmbed extends RyElement {
  setup(): void {
    this.#applyPlaceholderAria();
    this.#renderPlaceholder();
    this.on(this, 'click', this.#onActivate);
    this.on(this, 'keydown', this.#onKeydown);
  }

  get active(): boolean {
    return this.dataset.ryState === 'active';
  }

  play(): void {
    if (this.active) return;
    const src = this.#resolveUrl();
    if (!this.emit<PlayDetail>('beforeplay', { src })) return;
    this.#renderIframe(src);
    this.dataset.ryState = 'active';
    this.#applyActiveAria();
    this.emit<PlayDetail>('play', { src });
  }

  reset(): void {
    if (!this.active) return;
    this.#renderPlaceholder();
    delete this.dataset.ryState;
    this.#applyPlaceholderAria();
    this.emit('reset');
  }

  #onActivate = (): void => {
    if (this.active) return;
    this.play();
  };

  #onKeydown = (event: KeyboardEvent): void => {
    if (this.active) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.play();
    }
  };

  #renderPlaceholder(): void {
    const icon = this.getAttribute('icon') ?? '▶';
    const placeholderUrl = this.#resolvePlaceholder();
    const styleAttr = placeholderUrl
      ? ` style="background-image: url(&quot;${placeholderUrl}&quot;)"`
      : '';
    this.innerHTML = `
      <div data-ry-target="placeholder" class="ry-embed__placeholder"${styleAttr}>
        <span data-ry-target="icon" class="ry-embed__icon" aria-hidden="true">${icon}</span>
      </div>
    `;
  }

  #renderIframe(src: string): void {
    this.innerHTML = `
      <iframe
        data-ry-target="iframe"
        class="ry-embed__iframe"
        src="${src}"
        frameborder="0"
        allowfullscreen
        allow="autoplay; encrypted-media; picture-in-picture"
      ></iframe>
    `;
  }

  #resolveUrl(): string {
    const url = this.getAttribute('url');
    if (url) return url;
    const source = this.getAttribute('source');
    const id = this.getAttribute('id');
    const autoplay = this.hasAttribute('autoplay');
    if (source === 'youtube' && id) {
      const params = autoplay ? '?autoplay=1' : '';
      return `https://www.youtube.com/embed/${id}${params}`;
    }
    if (source === 'vimeo' && id) {
      const params = autoplay ? '?autoplay=1' : '';
      return `https://player.vimeo.com/video/${id}${params}`;
    }
    return '';
  }

  #resolvePlaceholder(): string {
    const placeholder = this.getAttribute('placeholder');
    if (placeholder) return placeholder;
    const source = this.getAttribute('source');
    const id = this.getAttribute('id');
    if (source === 'youtube' && id) {
      return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
    }
    return '';
  }

  #applyPlaceholderAria(): void {
    if (!this.hasAttribute('role') || this.getAttribute('role') === 'region') {
      this.setAttribute('role', 'button');
    }
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
    const label = this.getAttribute('aria-label');
    if (!label || label === 'Video player') {
      this.setAttribute('aria-label', 'Play video');
    }
  }

  #applyActiveAria(): void {
    if (this.getAttribute('role') === 'button') this.setAttribute('role', 'region');
    if (this.getAttribute('aria-label') === 'Play video') {
      this.setAttribute('aria-label', 'Video player');
    }
  }
}

customElements.define('ry-embed', RyEmbed);
